import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { sendBuyflowExit, isInsideWebView } from "../utils/nativeNavigation";
import { useAuthCredentials } from "../hooks/useAuthCredentials";
import { useOrderMedication } from "../hooks/useOrderMedication";
import { useInsuranceFulfillmentDetails } from "../hooks/useInsuranceFulfillmentDetails";
import { confirmPayment } from "../modules/refill/confirmPayment";
import { confirmDelivery } from "../modules/refill/confirmDelivery";
import { getPharmacyWebsiteLink } from "../modules/refill/pharmacyLink";
import { openDeeplink } from "../modules/care/utils";
import { FulfillmentPageShell } from "../components/prescriptions/FulfillmentPageShell";

type FulfillmentState = "pending_payment" | "pa_approved" | "pending_delivery";

const TITLE = "How your prescription is filled";
const PAYMENT_SUCCESS_TOAST = "Thanks — we've recorded your payment";
const DELIVERY_SUCCESS_TOAST = "Your medication delivery has been confirmed";
const ERROR_TOAST = "Something went wrong, try again";
const CARE_MESSAGE_DEEPLINK = "noom://noom.com/open?open=CARE_MESSAGE";

export const InsuranceFulfillmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawState = searchParams.get("state");
  const state: FulfillmentState =
    rawState === "pending_delivery" || rawState === "pa_approved"
      ? rawState
      : "pending_payment";
  const orderId = searchParams.get("orderId");

  // Independent facts, not one shared flag: PA can be approved while payment
  // is still pending, so each checkbox row tracks its own state.
  const isPendingDelivery = state === "pending_delivery";
  const isPaApproved = state === "pa_approved";
  const isPendingPayment = state === "pending_payment";
  const priorAuthApproved = !isPendingPayment;
  const paymentConfirmed = isPendingDelivery;

  const { accessCode, accessToken } = useAuthCredentials();
  const { medicationName, medicationImageUrl } = useOrderMedication(orderId);
  const { data: fulfillmentDetails } = useInsuranceFulfillmentDetails(orderId);

  const pharmacyName = fulfillmentDetails?.pharmacyDisplayName ?? "your pharmacy";
  const pharmacyLink = getPharmacyWebsiteLink(fulfillmentDetails?.fulfillmentPartner);
  const copayLabel = fulfillmentDetails
    ? `Complete your ${fulfillmentDetails.formattedPrice} copay`
    : "Complete your copay";
  const copayBody = fulfillmentDetails
    ? `Once your insurance coverage is verified, ${pharmacyName} will text you a secure link to pay your ${fulfillmentDetails.formattedPrice} copay.`
    : `Once your insurance coverage is verified, ${pharmacyName} will text you a secure link to pay your copay.`;
  const receiveBody = `After you pay, ${pharmacyName} will ship your medication. You can begin taking it as directed once it arrives.`

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const primaryDisabled = isPendingPayment
    ? false
    : submitting || !orderId || !accessCode || !accessToken;

  const handlePrimary = async () => {
    if (isPendingPayment) {
      openDeeplink(CARE_MESSAGE_DEEPLINK);
      return;
    }
    if (submittingRef.current) return;
    if (!orderId || !accessCode || !accessToken) return;
    submittingRef.current = true;
    setSubmitting(true);
    const apiCall = isPaApproved ? confirmPayment : confirmDelivery;
    const successToast = isPaApproved
      ? PAYMENT_SUCCESS_TOAST
      : DELIVERY_SUCCESS_TOAST;
    try {
      await apiCall(accessCode, accessToken, orderId);
      if (isInsideWebView()) {
        sendBuyflowExit({ toast: successToast });
      } else {
        window.alert(successToast);
        submittingRef.current = false;
        setSubmitting(false);
      }
    } catch {
      if (isInsideWebView()) {
        sendBuyflowExit({ toast: ERROR_TOAST });
      } else {
        window.alert(ERROR_TOAST);
      }
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    if (isInsideWebView()) {
      sendBuyflowExit();
    } else {
      navigate(-1);
    }
  };

  const steps = [
    {
      heading: "Noom verifies your coverage",
      body: "We'll verify your insurance coverage. If we need more information, we'll message you in the Care Team chat.",
      completed: priorAuthApproved,
    },
    {
      heading: copayLabel,
      body: copayBody,
      completed: paymentConfirmed,
    },
    {
      heading: "Receive your medication",
      body: receiveBody,
    },
  ];

  return (
    <FulfillmentPageShell
      title={TITLE}
      medicationName={medicationName}
      medicationImageUrl={medicationImageUrl}
      steps={steps}
      helpLink={pharmacyLink}
      helpLinkFallbackText="your pharmacy's website"
      showHelpRow={false}
      primaryLabel={
        isPendingDelivery
          ? "I received it"
          : isPaApproved
            ? "I've paid"
            : "Message care team"
      }
      onPrimary={handlePrimary}
      primaryDisabled={primaryDisabled}
      submitting={submitting}
      onDone={handleDone}
    />
  );
};
